import type {
  AggregationOption, BuilderLayoutConfig, CapabilityRegistryEntry, ChartCategory, FormatControl, FormatControlGroup, FormulaBuilderConfig, LabelValueOption,
  LayoutStepSection, LayoutStepsDefinition,
} from '../shared/self-service-config.types';

/**
 * Pure reads of the builder's JSON config (`dvm-layout-builder.json`): layout
 * types and their catalogue, the capability registry, per-layout step
 * sections / field labels, and the Format-control lists. The shell exposes
 * these through one-line members (they are part of the DvmBuilderHost
 * contract); steps may call the functions directly.
 */

// ── layout types / catalogue ────────────────────────────────────────────────
export function layoutTypesOf(layout: BuilderLayoutConfig): LabelValueOption[] {
  return layout.options?.layoutTypes?.length
    ? layout.options.layoutTypes
    : [
        { label: 'Waterfall', value: 'WATERFALL' },
        { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
      ];
}

export function chartCategoriesOf(layout: BuilderLayoutConfig): ChartCategory[] {
  return layout.options?.chartCatalogue ?? [];
}

export function categoryOfType(layout: BuilderLayoutConfig, type: string): string {
  return chartCategoriesOf(layout).find((c) => c.types.includes(type))?.id || '';
}

/** The catalogue category's types as picker options (unknown types get a bare label). */
export function typesForCategory(layout: BuilderLayoutConfig, category: ChartCategory): LabelValueOption[] {
  const layoutTypes = layoutTypesOf(layout);
  const byValue = new Map(layoutTypes.map((t) => [t.value, t]));
  return category.types
    .map((type) => byValue.get(type) || { label: type, value: type })
    .filter(Boolean);
}

// ── capability registry ─────────────────────────────────────────────────────
export function capabilitiesForType(layout: BuilderLayoutConfig, type: string): CapabilityRegistryEntry {
  return layout.options?.capabilityRegistry?.[type] ?? {};
}

/** An empty registry entry means "everything supported". */
export function chartSupports(layout: BuilderLayoutConfig, type: string, capability: string): boolean {
  const entry = capabilitiesForType(layout, type);
  if (!Object.keys(entry).length) return true;
  return entry[capability] === true;
}

export function chartExposesSection(layout: BuilderLayoutConfig, type: string, section: string): boolean {
  const sections = capabilitiesForType(layout, type).sections;
  return Array.isArray(sections) ? sections.includes(section) : true;
}

// ── per-layout step sections ────────────────────────────────────────────────
/** `layoutSteps[type]` — a string entry aliases another type's definition. */
export function layoutStepsFor(layout: BuilderLayoutConfig, layoutType: string): LayoutStepsDefinition | null {
  const map = layout.layoutSteps;
  if (!map) return null;
  let entry = map[layoutType];
  if (typeof entry === 'string') entry = map[entry];
  return entry && typeof entry !== 'string' ? entry : null;
}

export function layoutSectionOf(layout: BuilderLayoutConfig, layoutType: string, sectionId: string): LayoutStepSection | undefined {
  return layoutStepsFor(layout, layoutType)?.sections.find((section) => section.id === sectionId);
}

export function layoutSectionTitle(layout: BuilderLayoutConfig, layoutType: string, sectionId: string, fallback: string): string {
  return layoutSectionOf(layout, layoutType, sectionId)?.title || fallback;
}

export function layoutSectionHint(layout: BuilderLayoutConfig, layoutType: string, sectionId: string, fallback = ''): string {
  return layoutSectionOf(layout, layoutType, sectionId)?.hint ?? fallback;
}

/** A section without a `fields` list shows every field. */
export function fieldEnabled(layout: BuilderLayoutConfig, layoutType: string, sectionId: string, fieldKey: string): boolean {
  const section = layoutSectionOf(layout, layoutType, sectionId);
  if (!section || !section.fields) return true;
  return section.fields.some((field) => field.field === fieldKey);
}

export function fieldLabel(layout: BuilderLayoutConfig, layoutType: string, sectionId: string, fieldKey: string, fallback: string): string {
  const section = layoutSectionOf(layout, layoutType, sectionId);
  return section?.fields?.find((field) => field.field === fieldKey)?.label || fallback;
}

// ── option lists ────────────────────────────────────────────────────────────
const FONT_WEIGHT_OPTIONS_FALLBACK: LabelValueOption[] = [
  { label: 'Default', value: '' },
  { label: 'Normal', value: 'normal' },
  { label: 'Medium (500)', value: '500' },
  { label: 'Semi-bold (600)', value: '600' },
  { label: 'Bold', value: 'bold' },
];
export function fontWeightOptionsOf(layout: BuilderLayoutConfig): LabelValueOption[] {
  return layout.options?.fontWeights?.length ? layout.options.fontWeights : FONT_WEIGHT_OPTIONS_FALLBACK;
}

const MEASURE_AGGREGATIONS_FALLBACK: AggregationOption[] = [
  { label: 'None (raw value)', value: 'NONE' },
  { label: 'Sum', value: 'SUM' },
  { label: 'Average', value: 'AVG' },
  { label: 'Count', value: 'COUNT' },
];
/**
 * The Aggregation choices. Chart config files each add theirs and the loader
 * concatenates them, so the list is cut to one entry per value (the first one's
 * label and place); an entry's `params` - its extra settings - are kept from
 * whichever copy declares them.
 */
export function measureAggregationsOf(layout: BuilderLayoutConfig): AggregationOption[] {
  const listed = layout.options?.measureAggregations?.length ? layout.options.measureAggregations : MEASURE_AGGREGATIONS_FALLBACK;
  const byValue = new Map<string, AggregationOption>();
  for (const option of listed) {
    const seen = byValue.get(option.value);
    if (!seen) byValue.set(option.value, option);
    else if (!seen.params?.length && option.params?.length) byValue.set(option.value, { ...seen, params: option.params });
  }
  return [...byValue.values()];
}

const NUMBER_FORMATS_FALLBACK: LabelValueOption[] = [
  { label: 'Default', value: 'NONE' },
  { label: 'Commas (1,234)', value: 'THOUSANDS' },
  { label: 'Auto scale (K/M/B)', value: 'COMPACT' },
  { label: 'Thousands (K)', value: 'SCALE_THOUSANDS' },
  { label: 'Millions (M)', value: 'MILLIONS' },
  { label: 'Billions (B)', value: 'BILLIONS' },
  { label: 'Percent (12%)', value: 'PERCENT' },
];
export function numberFormatsOf(layout: BuilderLayoutConfig): LabelValueOption[] {
  return layout.options?.numberFormats?.length ? layout.options.numberFormats : NUMBER_FORMATS_FALLBACK;
}

export function formulaBuilderConfigOf(layout: BuilderLayoutConfig): FormulaBuilderConfig {
  return ((layout.options as Record<string, unknown> | undefined)?.['formulaBuilder'] as FormulaBuilderConfig | undefined) ?? {};
}

/** `enableFormulaReferences`: absent → on; boolean → global; map → per layout type. */
export function formulaReferencesEnabledFor(layout: BuilderLayoutConfig, layoutType: string): boolean {
  const config = layout.options?.enableFormulaReferences;
  if (config === undefined) return true;
  if (typeof config === 'boolean') return config;
  return config[layoutType] !== false;
}

/** Colours of a named palette from `options.palettes`. */
export function resolvePaletteColors(layout: BuilderLayoutConfig, paletteKey?: string): string[] | undefined {
  if (!paletteKey) return undefined;
  const palettes = (layout.options as Record<string, unknown> | undefined)?.['palettes'] as
    | Array<{ key?: string; colors?: string[] }>
    | undefined;
  const match = Array.isArray(palettes) ? palettes.find((p) => p.key === paletteKey) : undefined;
  return match?.colors?.length ? match.colors : undefined;
}

// ── Format controls ─────────────────────────────────────────────────────────
export const FORMAT_GROUP_ORDER = ['Title', 'Axis', 'Tooltip', 'Formatting', 'Interaction', 'Advanced'];

export function formatGroupOrderOf(layout: BuilderLayoutConfig): string[] {
  return layout.options?.formatGroupOrder?.length ? layout.options.formatGroupOrder : FORMAT_GROUP_ORDER;
}

/** Levels a per-level template expands to when the caller does not know the table's depth. */
export const DEFAULT_FORMAT_LEVELS = 3;

export function formatControlsOf(layout: BuilderLayoutConfig, levelCount = DEFAULT_FORMAT_LEVELS): FormatControl[] {
  // The config files carry `{ "// note": "…" }` comment entries in the list;
  // they have no key, and were drawn as a nameless box in a stray "Format" group.
  const controls = (layout.options?.formatControls ?? []).filter((control) => typeof control?.key === 'string' && !!control.key);
  return expandPerLevelControls(controls, levelCount);
}

/**
 * Repeat every `perLevel` template once per level, `{n}` → 1…levelCount, so a
 * table with 6 levels gets Level 1 … Level 6 groups. Templates expand in level
 * order, so the groups come out in order too.
 */
export function expandPerLevelControls(controls: FormatControl[], levelCount: number): FormatControl[] {
  const count = Math.max(1, Math.floor(levelCount) || DEFAULT_FORMAT_LEVELS);
  const levels = Array.from({ length: count }, (_, index) => String(index + 1));
  const expanded: FormatControl[] = [];
  const templates = controls.filter((control) => control.perLevel);
  let templatesDone = false;
  for (const control of controls) {
    if (!control.perLevel) {
      expanded.push(control);
      continue;
    }
    // All templates expand together at the first one's place, level by level,
    // so each "Level n" group holds all of its settings in config order.
    if (templatesDone) continue;
    templatesDone = true;
    for (const n of levels) {
      const fill = (text: string | undefined) => text?.split('{n}').join(n);
      for (const template of templates) {
        expanded.push({ ...template, key: fill(template.key)!, label: fill(template.label)!, group: fill(template.group), perLevel: undefined });
      }
    }
  }
  return expanded;
}

/**
 * Enabled Format controls that apply to `layoutType`. GROUP and DROPDOWN only
 * take controls that name them explicitly; other layouts take unscoped
 * controls too, with a layout-specific control winning over an unscoped one
 * of the same key.
 */
export function applicableFormatControls(layout: BuilderLayoutConfig, layoutType: string, levelCount = DEFAULT_FORMAT_LEVELS): FormatControl[] {
  const explicitOnly = layoutType === 'GROUP' || layoutType === 'DROPDOWN';
  const applicable = formatControlsOf(layout, levelCount).filter(
    (control) =>
      control.enabled !== false &&
      (explicitOnly
        ? !!control.appliesTo?.includes(layoutType)
        : !control.appliesTo || !control.appliesTo.length || control.appliesTo.includes(layoutType)),
  );
  const winners = new Map<string, FormatControl>();
  applicable.forEach((control) => {
    const prev = winners.get(control.key);
    const specific = !!(control.appliesTo && control.appliesTo.length);
    const prevSpecific = !!(prev && prev.appliesTo && prev.appliesTo.length);
    if (!prev || (specific && !prevSpecific)) winners.set(control.key, control);
  });
  return applicable.filter((control) => winners.get(control.key) === control);
}

/** `visibleWhen: { key, values }` gates a control on another control's value. */
export function formatControlVisible(control: FormatControl, format: Record<string, unknown> | undefined): boolean {
  const rule = control.visibleWhen;
  if (!rule) return true;
  const value = format?.[rule.key];
  return rule.values.some((candidate) => candidate === value);
}

/** `visibleWhenType` restricts a table control to given table sub-types. */
export function controlVisibleForSubType(control: FormatControl, subType: string): boolean {
  if (!subType) return true;
  const allowed = control.visibleWhenType;
  return !Array.isArray(allowed) || !allowed.length || allowed.includes(subType);
}

/** Controls → accordion groups in config order (unknown groups after, in first-seen order); `exposes` drops hidden sections. */
export function groupFormatControls(controls: FormatControl[], order: string[], exposes: (group: string) => boolean = () => true): FormatControlGroup[] {
  const byGroup = new Map<string, FormatControl[]>();
  const seen: string[] = [];
  controls.forEach((control) => {
    const group = control.group || 'Format';
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      seen.push(group);
    }
    byGroup.get(group)!.push(control);
  });
  seen.sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
  });
  return seen.filter(exposes).map((name) => ({ name, controls: byGroup.get(name)! }));
}

// ── defaults ────────────────────────────────────────────────────────────────
/** Built-in layout used until (or when) `dvm-layout-builder.json` loads. */
export const DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT: BuilderLayoutConfig = {
  version: '1.0',
  builder: 'dvm-layout',
  steps: [
    { id: 'workbench', label: 'DVM Layout Workbench', sections: [{ id: 'workbench', fields: [{ field: 'dvmLayoutWorkbench' }] }] },
  ],
  options: {
    layoutTypes: [
      { label: 'Waterfall', value: 'WATERFALL' },
      { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
      { label: 'Horizontal Bar Chart', value: 'HORIZONTAL_BAR_CHART' },
      { label: 'Input Box', value: 'INPUT_BOX' },
      { label: 'Slider', value: 'SLIDER' },
    ],
    stepTypes: [
      { label: 'Start (anchor pillar)', value: 'START' },
      { label: 'Change (floating segment)', value: 'CHANGE' },
      { label: 'Total (subtotal / milestone)', value: 'TOTAL' },
    ],
    layoutAggregations: [
      { label: 'Sum', value: 'SUM' },
      { label: 'Average', value: 'AVG' },
    ],
    signOptions: [
      { label: 'Auto (let the query decide)', value: 'AUTO' },
      { label: 'Positive (adds)', value: 'POSITIVE' },
      { label: 'Negative (subtracts)', value: 'NEGATIVE' },
    ],
    measureAggregations: [
      { label: 'None (raw value)', value: 'NONE' },
      { label: 'Sum', value: 'SUM' },
      { label: 'Average', value: 'AVG' },
      { label: 'Count', value: 'COUNT' },
    ],
    rollUpStrategies: [
      { label: 'Sum', value: 'SUM' },
      { label: 'Average', value: 'AVG' },
      { label: 'Minimum', value: 'MIN' },
      { label: 'Maximum', value: 'MAX' },
    ],
    entityGroup: 'NontimeSeriesEntities',
    hiddenColumns: ['uuid', '__rowKey', 'source_row_hash', 'sourced_row_hash', 'spriced_row_hash'],
  },
  defaults: {
    componentType: 'CHART',
    dashboardId: 101,
  },
};

/** `componentId` prefix per layout type (`CMP-<FAMILY>-…`); unknown types get the bare `CMP`. */
const COMPONENT_ID_PREFIXES: Record<string, string> = {
  WATERFALL: 'CMP-WATERFALL',
  DRILL_DOWN_TABLE: 'CMP-DRILLDOWN',
  NESTED_TABLE: 'CMP-NESTED',
  HORIZONTAL_BAR_CHART: 'CMP-HBAR',
  KPI: 'CMP-KPI',
  BAR: 'CMP-BAR',
  SCATTER_BUBBLE: 'CMP-SCATTER',
  LINE_AREA: 'CMP-LINE',
  PIE_COMPOSITION: 'CMP-PIE',
  TABLE_MATRIX: 'CMP-TMATRIX',
  DROPDOWN: 'CMP-DROPDOWN',
  FILE_UPLOAD: 'CMP-FILEUPLOAD',
  POPUP: 'CMP-POPUP',
};

export function componentIdPrefixFor(layoutType: string): string {
  return COMPONENT_ID_PREFIXES[layoutType] || 'CMP';
}
